import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, CheckCircle2, Eye, EyeOff, KeyRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, formatApiError } from "@/lib/api";

/**
 * ResetPasswordPage — token-in-query broker password reset.
 *
 * Consumes ?token=... from the email link and POSTs it plus the new
 * password. Success → confirmation state with a "Back to sign in"
 * button. Invalid / expired token → friendly error state + shortcut
 * back to the forgot-password form.
 */
const MIN_PW_LEN = 8;

function scorePassword(pw) {
  if (!pw) return { label: "", pct: 0, tone: "gray" };
  let score = 0;
  if (pw.length >= MIN_PW_LEN) score += 1;
  if (pw.length >= 12) score += 1;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score += 1;
  if (/\d/.test(pw)) score += 1;
  if (/[^A-Za-z0-9]/.test(pw)) score += 1;
  const buckets = [
    { label: "Too short", pct: 20, tone: "red" },
    { label: "Weak", pct: 40, tone: "red" },
    { label: "Fair", pct: 60, tone: "amber" },
    { label: "Good", pct: 80, tone: "green" },
    { label: "Strong", pct: 100, tone: "green" },
  ];
  return buckets[Math.min(score, buckets.length) - 1] || buckets[0];
}

export default function ResetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [pw, setPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [tokenError, setTokenError] = useState("");

  const strength = scorePassword(pw);
  const strengthColor =
    strength.tone === "green"
      ? "bg-brand-green"
      : strength.tone === "amber"
        ? "bg-amber-400"
        : strength.tone === "red"
          ? "bg-red-400"
          : "bg-gray-200";

  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < MIN_PW_LEN) {
      toast.error(`Password must be at least ${MIN_PW_LEN} characters.`);
      return;
    }
    if (pw !== confirmPw) {
      toast.error("Passwords do not match.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail === "INVALID_OR_USED_TOKEN" || detail === "TOKEN_EXPIRED") {
        setTokenError(detail);
      } else {
        toast.error(formatApiError(err, "Reset failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  // No token in URL at all → same friendly error state.
  if (!token || tokenError) {
    const isExpired = tokenError === "TOKEN_EXPIRED";
    return (
      <div className="mb-page flex min-h-screen items-center justify-center px-6 py-16">
        <div
          className="w-full max-w-md rounded-3xl border border-gray-100 bg-white p-10 text-center shadow-mb-card mb-rise"
          data-testid="reset-password-invalid"
        >
          <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-red-50 text-red-500">
            <AlertTriangle className="h-5 w-5" />
          </span>
          <h1 className="mt-6 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
            {isExpired ? "This link has expired." : "This link is no longer valid."}
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-gray-500">
            {isExpired
              ? "Password reset links expire after 30 minutes for your security."
              : "Reset links can only be used once. Request a fresh one and try again."}
          </p>
          <div className="mt-8 space-y-3">
            <Link to="/forgot-password" data-testid="reset-password-request-again">
              <Button className="mb-focus-ring h-12 w-full rounded-xl bg-[#1B2B4B] text-base font-semibold text-white hover:bg-[#14213A]">
                Request a new link
              </Button>
            </Link>
            <Link
              to="/login"
              className="mb-focus-ring inline-block text-sm text-gray-500 hover:text-gray-900"
              data-testid="reset-password-back-to-login"
            >
              Back to sign in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="mb-page flex min-h-screen items-center justify-center px-6 py-16">
        <div
          className="w-full max-w-md rounded-3xl border border-gray-100 bg-white p-10 text-center shadow-mb-card mb-rise"
          data-testid="reset-password-success"
        >
          <span className="mx-auto inline-flex h-12 w-12 items-center justify-center rounded-full bg-brand-green/10 text-brand-green">
            <CheckCircle2 className="h-5 w-5" />
          </span>
          <h1 className="mt-6 text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
            Password updated.
          </h1>
          <p className="mt-3 text-sm leading-relaxed text-gray-500">
            You can now sign in with your new password. We&apos;ve also saved
            a security notice to your in-app notifications.
          </p>
          <div className="mt-8">
            <Button
              onClick={() => navigate("/login", { replace: true })}
              className="mb-focus-ring h-12 w-full rounded-xl bg-[#1B2B4B] text-base font-semibold text-white hover:bg-[#14213A]"
              data-testid="reset-password-go-to-login"
            >
              Back to sign in
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-page flex min-h-screen items-center justify-center px-6 py-16">
      <div
        className="w-full max-w-md rounded-3xl border border-gray-100 bg-white p-10 shadow-mb-card mb-rise"
        data-testid="reset-password-page"
      >
        <div className="mb-8 flex items-center">
          <Link
            to="/login"
            className="mb-focus-ring inline-flex items-center rounded-lg"
          >
            <BrandLogo className="h-9" />
          </Link>
        </div>

        <div className="mb-6 inline-flex items-center gap-1.5 rounded-full border border-brand-navy/15 bg-brand-navy/5 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-brand-navy">
          <KeyRound className="h-3 w-3" />
          Choose a new password
        </div>

        <h1 className="text-2xl font-bold tracking-tight text-gray-900 sm:text-3xl">
          Set your new password.
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-gray-500">
          Choose something strong — mix upper &amp; lower case, a number, and
          a symbol.
        </p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="reset-password-form">
          <div className="space-y-2">
            <Label htmlFor="rp-new" className="text-gray-700">
              New password
            </Label>
            <div className="relative">
              <Input
                id="rp-new"
                type={show ? "text" : "password"}
                required
                minLength={MIN_PW_LEN}
                autoComplete="new-password"
                value={pw}
                onChange={(e) => setPw(e.target.value)}
                className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white pr-12 text-base"
                data-testid="reset-password-new"
              />
              <button
                type="button"
                onClick={() => setShow((v) => !v)}
                aria-label={show ? "Hide password" : "Show password"}
                className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-700"
                data-testid="reset-password-toggle"
              >
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            {pw && (
              <div data-testid="reset-password-strength">
                <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    className={`h-full ${strengthColor} transition-all duration-300`}
                    style={{ width: `${strength.pct}%` }}
                  />
                </div>
                <div className="mt-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">
                  {strength.label}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="rp-confirm" className="text-gray-700">
              Confirm new password
            </Label>
            <Input
              id="rp-confirm"
              type={show ? "text" : "password"}
              required
              minLength={MIN_PW_LEN}
              autoComplete="new-password"
              value={confirmPw}
              onChange={(e) => setConfirmPw(e.target.value)}
              className="mb-focus-ring h-12 rounded-xl border-gray-200 bg-white text-base"
              data-testid="reset-password-confirm"
            />
            {confirmPw && confirmPw !== pw && (
              <p className="text-xs text-red-500" data-testid="reset-password-mismatch">
                Passwords don&apos;t match yet.
              </p>
            )}
          </div>

          <Button
            type="submit"
            disabled={busy || pw.length < MIN_PW_LEN || pw !== confirmPw}
            className="mb-focus-ring h-12 w-full rounded-xl bg-[#1B2B4B] text-base font-semibold text-white hover:bg-[#14213A] disabled:opacity-60"
            data-testid="reset-password-submit"
          >
            {busy ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Updating password…
              </>
            ) : (
              "Update password"
            )}
          </Button>
        </form>
      </div>
    </div>
  );
}
